<template>
  <SlottedRow
    :allow-delete="editing && displayField.allowDelete"
    :uuid="displayField.uuid"
    :handle-delete="handleDelete"
  >
    <template #label>
      <DisplayFieldTextarea
        :value="displayField.label || 'Address'"
        data-testid="label-textarea"
        :editing="editing && displayField.allowLabelEdit"
      />
    </template>
    <template
      v-if="displayField.citation"
      #icon
    >
      <CitationIcon
        :display-field="displayField"
      />
    </template>
    <template #value>
      <div
        v-if="values[valueKeys[0]] || editing"
        class="address-display-value-wrapper"
      >
        <DisplayFieldTextarea
          v-model="values[valueKeys[0]]"
          :placeholder="editing ? Object.keys(values)[0] : undefined"
          :editing="editing && displayField.allowEdit"
          :style="editing ? `margin-left: 3px` : null"
          data-testid="address-textarea"
          @blur="handleBlur"
        />
      </div>
      <div
        v-if="editing"
        id="editing-city-state-div"
        class="address-editing-wrapper"
      >
        <DisplayFieldTextarea
          v-model="values[valueKeys[1]]"
          placeholder="City"
          :editing="displayField.allowEdit"
          data-testid="city-textarea"
          @blur="handleBlur"
        />
        <DisplayFieldTextarea
          v-model="values[valueKeys[2]]"
          placeholder="State"
          :editing="displayField.allowEdit"
          data-testid="state-textarea"
          @blur="handleBlur"
        />
      </div>
      <div
        v-else
        class="address-city-state-line-wrapper"
      >
        <DisplayFieldTextarea
          v-model="cityStateLine"
          data-testid="city-state-textarea"
        />
      </div>
    </template>
  </SlottedRow>
</template>

<script>
import {DisplayField, extractField} from '@/lib/classes/InfoWindowContent';
import SlottedRow from './SlottedRow.vue';
import DisplayFieldTextarea from './DisplayFieldTextarea.vue';
import CitationIcon from '@/components/InfoWindow/DisplayFields/CitationIcon.vue';

export default {
  name: 'AddressDisplay',
  components: {
    SlottedRow,
    DisplayFieldTextarea,
    CitationIcon,
  },
  props: {
    displayField: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['deleteDisplayField', 'updateDisplayField'],
  data() {
    return {
      values: {},
      valueKeys: [],
    };
  },
  computed: {
    cityStateLine() {
      const city = this.values['City'];
      const state = this.values['State'];
      const cityState = [city, state].filter(Boolean).join(', ');
      // Parcels saved before the rename store the zip as 'Zip'
      const zip = this.values['Zip Code'] ?? this.values['Zip'];

      if (!zip) return cityState;
      if (!cityState) return zip;
      return `${cityState} ${zip}`;
    },
  },
  mounted() {
    this.values = this.displayField.displayFields.reduce((acc, field) => {
      acc[field.label] = field.value;
      return acc;
    }, {});
    this.valueKeys = Object.keys(this.values);

    Object.keys(this.values).forEach((fieldName) => this.values[fieldName] =
      extractField(this.displayField, fieldName).value ?? null,
    );
  },
  methods: {
    handleDelete() {
      this.$emit('deleteDisplayField', this.displayField);
    },
    handleBlur() {
      if (!this.editing) return;

      const displayField = new DisplayField({...this.displayField});

      // Update the nested displayFields array with new values
      displayField.displayFields = this.displayField.displayFields.map((field) => {
        const newValue = this.values[field.label];
        if (newValue !== field.value) {
          // Create a new instance of the field with updated value, preserving other properties
          return new DisplayField({
            ...field,
            value: newValue,
          });
        }
        return field;
      });

      this.$emit('updateDisplayField', displayField);
    },
  },
};
</script>

<style lang="scss" scoped>
  #editing-city-state-div {
    display: grid;
    grid-template-columns: 10fr 3fr;
    gap: 5px;
    margin-top: 4px;
  }

  .address-display-value-wrapper {
    position: relative;
    margin-bottom: -2px;
    margin-left: -3px;
  }

  .address-city-state-line-wrapper {
    margin-left: -3px;
  }
  .address-editing-wrapper {
    gap: 2px;
  }
</style>
